/**
 * HTTP boundary of the «Caché» page (Administración → Caché, 2026-10-26).
 *
 * Mirrors `builder/api/routes/cache.py` and `builder/service_cache.py`.
 * Two shapes carry meaning and invent nothing:
 *
 * 1. **`last: null` means "no reload measured this report yet"**, not
 *    zero seconds. The page shows «sin datos» for it and leaves it out of
 *    the totals (saying how many were left out).
 * 2. **`last_warm: null` means the project never ran a pre-publish warm**
 *    (no reload since the feature), not a success.
 *
 * Its own module, like `scope-ops-api.ts`: everything routes through
 * `api`, which injects the JWT and prefixes `/{tenant}/`.
 */

import { api } from "@/lib/api";
import type { DisplayLabel } from "@pervasivemind/dashboard-render";

export interface CacheButton {
  key: string;
  label: DisplayLabel | null;
  /** How many fixed options the button has. */
  options: number;
}

export interface CacheReportLast {
  /** Seconds of computation of the report in the last warm. */
  seconds: number | null;
  /** Variants (filter states) the last warm computed. */
  variants: number | null;
  /** Locales per variant in the last warm (1 when the SQL ignores it). */
  locales: number | null;
  errors: number;
}

export interface CacheReport {
  key: string;
  name: DisplayLabel;
  group_key: string | null;
  /** The group's options report (`/report-groups/{key}/filters`). Its
   *  option is derived: on when any tab of the group has it on. */
  is_group_options: boolean;
  buttons: CacheButton[];
  reads_locale: boolean;
  enabled: boolean;
  /** Variants the warm runs with the option on (defaults included). */
  variants_if_enabled: number;
  last: CacheReportLast | null;
}

export interface CacheLastWarm {
  job_id: string;
  status: "completed" | "failed" | string;
  finished_at: string | null;
  seconds: number | null;
  files: number | null;
  errors: number | null;
  over_threshold: boolean;
  error_summary: string | null;
}

export interface CacheProject {
  slug: string;
  /** `projects.name`: a plain string or a JSON-encoded LocalizedString. */
  name: string;
  /** `{report key: error}` for a published report that does not build. */
  broken: Record<string, string>;
  reports: CacheReport[];
  last_warm: CacheLastWarm | null;
}

export interface CacheSettings {
  locales: string[];
  supported_locales: string[];
  alert_minutes: number;
  updated_by: string;
  updated_at: string | null;
  /** BUILDER_WARM_WORKERS: variants computed at a time. */
  workers: number;
}

export interface CacheOverview {
  settings: CacheSettings;
  projects: CacheProject[];
}

export const cacheApi = {
  overview: () => api.get<CacheOverview>("/api/builder/cache"),
  saveSettings: (body: { locales: string[]; alert_minutes: number }) =>
    api.put<{ locales: string[]; alert_minutes: number }>(
      "/api/builder/cache/settings",
      body,
    ),
  setReportOption: (key: string, enabled: boolean) =>
    api.put<{ key: string; enabled: boolean }>(
      `/api/builder/cache/reports/${encodeURIComponent(key)}`,
      { enabled },
    ),
};
